import { X } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  type FormEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/button';

// Edits and actions open in place instead of in a popup: the section the person is looking at turns into its form,
// and the rest of the page moves to make room. Use InlinePanel for forms and InlineConfirm before something that
// cannot be undone.

const FOCUSABLE = 'input, textarea, select, button[role="combobox"], [contenteditable="true"]';

interface InlinePanelProps {
  title: string;
  description?: ReactNode;
  /** Escape and the close button call this */
  onClose: () => void;
  /** Renders the panel as a form */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** `danger` for rejecting, reporting and similar */
  tone?: 'default' | 'danger';
  /** Accessible name of the close button */
  closeLabel?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A form or action that opens where it was started. It slides open, scrolls into view and focuses its first field;
 * Escape closes it. Render it only while open.
 */
export function InlinePanel({
  title,
  description,
  onClose,
  onSubmit,
  tone = 'default',
  closeLabel = 'Close',
  children,
  className,
}: InlinePanelProps) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();

  useEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    panel.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    const field = panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
    field.focus({ preventScroll: true });
  }, []);

  const onKeyDown = (event: KeyboardEvent) => {
    // Let open menus (Select, dropdowns) close first; they stop the event themselves
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.stopPropagation();
      onClose();
    }
  };

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="grid min-w-0 gap-0.5">
          <h2 id={titleId} className="font-semibold">
            {title}
          </h2>
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-mr-2 -mt-2 shrink-0"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <X aria-hidden />
        </Button>
      </div>
      {children}
    </>
  );

  const shared = {
    'aria-labelledby': titleId,
    tabIndex: -1,
    onKeyDown,
    className: cn(
      'grid scroll-mt-20 gap-4 rounded-xl border bg-surface p-4 text-surface-foreground shadow-md outline-none',
      'animate-inline-open motion-reduce:animate-none',
      tone === 'danger' ? 'border-danger/50' : 'border-primary/60',
      className,
    ),
  };

  return onSubmit ? (
    <form ref={ref as Ref<HTMLFormElement>} noValidate onSubmit={onSubmit} {...shared}>
      {body}
    </form>
  ) : (
    <section ref={ref} {...shared}>
      {body}
    </section>
  );
}

/** Cancel and submit buttons at the bottom of an InlinePanel */
export const InlinePanelFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-wrap justify-end gap-2', className)} {...props} />
);

interface InlineConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Name the action: "Withdraw proposal", not "OK" */
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  className?: string;
}

/** Asks before something that cannot be undone, right where it was started. Never use window.confirm. */
export function InlineConfirm({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  className,
}: InlineConfirmProps) {
  if (!open) return null;
  return (
    <InlinePanel
      title={title}
      description={description}
      onClose={() => onOpenChange(false)}
      tone={destructive ? 'danger' : 'default'}
      closeLabel={cancelLabel}
      className={className}
    >
      <InlinePanelFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
